import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { TokenService } from '@utilities/services/token.service';
import { Observable, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ModalService } from '../../shared/services/modal.service';
import { LoginRequest } from '../interfaces/login-request.interface';
import { LoginResponse } from '../interfaces/login-response.interface';
import { LoginAsGuestRequest } from '../../external-home/interfaces/login-as-guest-request';
import { SharedLoginResponse } from '../interfaces/shared-login-response.interface';
import { interact } from '@okta/okta-auth-js';
import { Router } from '@angular/router';
import { LoginRedirectRequest, LoginRedirectResponse } from '../interfaces/login-redirect-request.interface';

const API_URL = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private modalService = inject(ModalService);
  private STORAGE_KEY = 'shared-auth';
  private router = inject(Router);
  private _authResp = signal<SharedLoginResponse | null>(null);
  authResp = computed(() => this._authResp());

  constructor() {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as SharedLoginResponse;
        this._authResp.set(parsed);
      } catch (e) {
        console.error('Error parseando shared-auth de localStorage', e);
        localStorage.removeItem(this.STORAGE_KEY);
      }
    }
  }

  setAuthResp(resp: SharedLoginResponse) {
    this._authResp.set(resp);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(resp));
  }

  sharedLogout() {
    this._authResp.set(null);
    localStorage.removeItem(this.STORAGE_KEY);
    this.tokenService.removeTokenKeys();
    this.router.navigateByUrl("/");
  }

  isLoggedIn(): boolean {
    let token = this.tokenService.getTokenIncog();
    if (!token){ 
      token = this.tokenService.getToken();
    }
    if (!token) return false;
    
    const isTokenExpired = this.tokenService.isTokenExpired();

    if (isTokenExpired) {
      this.logout();
      return false;
    }

    return true;
  }

  login(request: LoginRequest): Observable<boolean> {
    return this.http.post<LoginResponse>(`${API_URL}/login`, request).pipe(
      map((response) => this.handleAuthSuccess(response)),
      catchError((err) => {
        this.modalService.showErrorModal(err);

        return of(false);
      })
    );
  }

  logout(): void {
    this.tokenService.removeTokenKeys();
  }

  private handleAuthSuccess({ accessToken }: LoginResponse): boolean {
    const token = this.tokenService.getToken();
    this.tokenService.saveToken(accessToken);
    this.tokenService.saveTokenExpiration();
    this.tokenService.saveTokenPermission();
    return true;
  }

  loginAsGuest(request: LoginAsGuestRequest): Observable<SharedLoginResponse> {
    return this.http.post<SharedLoginResponse>(`${API_URL}/sharedlogin`, request);
  }

  loginRedirect(request: LoginRedirectRequest): Observable<LoginRedirectResponse> {
    return this.http.post<LoginResponse>(`${API_URL}/loginImpersonationSession`, request).pipe(
      map((response) => {
        this.handleAuthSuccessIncog(response);
        return { ok: true } as LoginRedirectResponse;
      }),
      catchError((err) => {
        const msg =
          err?.error?.detail ||
          err?.error?.message ||
          err?.message ||
          'No fue posible validar el acceso.';

        return of({ ok: false, message: msg } as LoginRedirectResponse);
      })
    );
  }

  private handleAuthSuccessIncog({ accessToken }: LoginResponse): boolean {
      this.tokenService.saveTokenIncog(accessToken);
      this.tokenService.saveTokenExpiration();
      this.tokenService.saveTokenPermission();
      this.tokenService.saveTokenRedirect();
    return true;
  }
}